import { Box, Button, Stack, Typography } from "@mui/material";
import { isEmpty, isNil } from "lodash-es";
import { useState } from "react";

import PageTabContent from "@/App/Layouts/PageBody/PageTabContent";
import PageTabHeader from "@/App/Layouts/PageBody/PageTabHeader";
import ViewContentBlock from "@/App/Layouts/ViewContentBlock";
import { ViewLayout } from "@/App/Layouts/ViewLayout";
import SubscriptionPlanOptionList from "@/App/MainAppView/Products/AssetSubscriptionPlans/components/SubscriptionPlanOptionList";
import NoDataAlert from "@/common/components/AppAlerts/NoDataAlert";
import NoFilesAlert from "@/common/components/AppAlerts/NoFilesAlert";
import AssetSpecDisplay from "@/common/components/Entity/Asset/AssetSpecDisplay";
import GeneralDiscountDisplay from "@/common/components/Entity/General/GeneralDiscount/GeneralDiscountDisplay";
import EntityChangedByInfoCard from "@/common/components/EntityInfo/EntityChangedByInfoCard";
import FileListView from "@/common/components/Files/FileListView";
import DurationDisplay from "@/common/components/Form/Display/DurationDisplay";
import FieldValue from "@/common/components/Form/Display/FieldValue";
import { BooleanStatusDisplay } from "@/common/components/Status/BooleanStatusDisplay";
import { FileItem } from "@/common/fileItem";
import { renderIf } from "@/common/helpers/render/renderIf";
import { ViewVariant } from "@/common/ts/layout";
import { AppPermission, AssetSubscriptionPlanDto, EntityType } from "@/core/api/generated/v1";

import EntityAffiliationInfoBlock from "../../../EntityAffiliation/EntityAffiliationInfoBlock";
import GeneralCountryDisplay from "../../../General/Display/GeneralCountryDisplay";
import AssetSubscriptionPlanToggleEnabledModal from "../../AssetSubscriptionPlanToggleEnabledModal";
import SubscriptionPlanPriceDisplay from "../../SubscriptionPlanPriceDisplay";

interface Props {
  assetSubscriptionPlan: AssetSubscriptionPlanDto;
  onUpdate: (newValue?: AssetSubscriptionPlanDto) => void;
}

export default function OverviewTabContent({ assetSubscriptionPlan, onUpdate }: Props) {
  const [isToggleEnabledModalOpen, setIsToggleEnabledModalOpen] = useState(false);

  return (
    <PageTabContent>
      <ViewLayout
        displayProps={{ viewVariant: ViewVariant.Tab }}
        header={
          <PageTabHeader
            title={"Details"}
            subtitle2={<EntityChangedByInfoCard entity={assetSubscriptionPlan} />}
          />
        }
      >
        <ViewContentBlock>
          <FieldValue label='Name' isEmpty={!assetSubscriptionPlan?.name}>
            {assetSubscriptionPlan?.name}
          </FieldValue>

          <FieldValue label='Description' isEmpty={!assetSubscriptionPlan?.description}>
            {assetSubscriptionPlan?.description}
          </FieldValue>

          <FieldValue label='Status'>
            <Stack direction='row' spacing={2} sx={{ alignItems: "center" }}>
              <BooleanStatusDisplay
                value={assetSubscriptionPlan?.isEnabled}
                statusProps={{
                  variant: "default",
                  title: assetSubscriptionPlan?.isEnabled ? "Enabled" : "Disabled",
                  indicatorProps: {
                    size: "medium",
                  },
                }}
              />

              <Button
                variant='contained'
                color={assetSubscriptionPlan?.isEnabled ? "error" : "success"}
                size='small'
                onClick={() => setIsToggleEnabledModalOpen(true)}
              >
                {assetSubscriptionPlan?.isEnabled ? "Disable" : "Enable"}
              </Button>
            </Stack>
          </FieldValue>

          <FieldValue label='Country' isEmpty={!assetSubscriptionPlan?.country}>
            <GeneralCountryDisplay country={assetSubscriptionPlan?.country} withDetailsPopover />
          </FieldValue>
        </ViewContentBlock>

        {/* Affiliation */}
        <EntityAffiliationInfoBlock
          entityType={EntityType.AssetSubscriptionPlan}
          entityId={assetSubscriptionPlan.id}
          entity={assetSubscriptionPlan}
          entityUpdatePermission={AppPermission.AssetSubscriptionPlanManage}
          infoDisplayProps={{
            withoutTenant: true,
          }}
          onUpdate={onUpdate}
        />

        {/* Asset spec */}
        <ViewContentBlock>
          <Typography variant='h3'>Asset spec</Typography>

          {!assetSubscriptionPlan?.assetSpec && <NoDataAlert />}

          <AssetSpecDisplay
            tenantId={assetSubscriptionPlan?.tenantId}
            spec={assetSubscriptionPlan?.assetSpec}
          />
        </ViewContentBlock>

        {/* Attachments */}
        <ViewContentBlock>
          <Typography variant='h3'>Attachments</Typography>

          <Stack spacing={2}>
            <Typography component='div' variant='subtitle2'>
              Image
            </Typography>

            {renderIf()
              .if(!!assetSubscriptionPlan?.image)
              .then(
                <FileListView
                  files={FileItem.createManyFrom(
                    assetSubscriptionPlan?.image ? [assetSubscriptionPlan.image] : undefined,
                  )}
                  maxVisibleFiles={10}
                  itemWidth={250}
                  itemHeight={190}
                  sx={{ mt: 2, width: "100%" }}
                />,
              )
              .else(<NoFilesAlert title='No image' description={undefined} />)
              .render()}
          </Stack>

          <Stack spacing={2}>
            <Typography component='div' variant='subtitle2'>
              Images
            </Typography>

            {renderIf()
              .if(!!assetSubscriptionPlan?.images && assetSubscriptionPlan.images.length !== 0)
              .then(
                <FileListView
                  files={FileItem.createManyFrom(assetSubscriptionPlan?.images)}
                  maxVisibleFiles={10}
                  itemWidth={250}
                  itemHeight={190}
                  sx={{ mt: 2, width: "100%" }}
                />,
              )
              .else(<NoFilesAlert title='No images' description={undefined} />)
              .render()}
          </Stack>

          <Stack spacing={2}>
            <Typography variant='subtitle2'>Attachments</Typography>

            {renderIf()
              .if(
                !!assetSubscriptionPlan?.attachments &&
                  assetSubscriptionPlan.attachments.length !== 0,
              )
              .then(
                <FileListView
                  files={FileItem.createManyFrom(assetSubscriptionPlan?.attachments)}
                  maxVisibleFiles={10}
                  itemWidth={250}
                  itemHeight={190}
                  sx={{ mt: 2, width: "100%" }}
                />,
              )
              .else(<NoFilesAlert title='No attachments' description={undefined} />)
              .render()}
          </Stack>
        </ViewContentBlock>

        {/* Settings */}
        <ViewContentBlock>
          <Typography variant='h3'>Settings</Typography>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xxs: "1fr", md: `repeat(4, 1fr)` },
              gridTemplateRows: "auto",
              rowGap: 1,
              columnGap: 1,
            }}
          >
            <FieldValue
              label='Min duration'
              isEmpty={!assetSubscriptionPlan?.settings?.minDuration}
            >
              <DurationDisplay value={assetSubscriptionPlan?.settings?.minDuration} />
            </FieldValue>

            <FieldValue
              label='Max duration'
              isEmpty={!assetSubscriptionPlan?.settings?.maxDuration}
            >
              <DurationDisplay value={assetSubscriptionPlan?.settings?.maxDuration} />
            </FieldValue>

            <FieldValue
              label='Exact duration'
              isEmpty={!assetSubscriptionPlan?.settings?.exactDuration}
            >
              <DurationDisplay value={assetSubscriptionPlan?.settings?.exactDuration} />
            </FieldValue>

            <FieldValue
              label='Default duration'
              isEmpty={!assetSubscriptionPlan?.settings?.defaultDuration}
            >
              <DurationDisplay value={assetSubscriptionPlan?.settings?.defaultDuration} />
            </FieldValue>
          </Box>
        </ViewContentBlock>

        {/* Prices */}
        <ViewContentBlock>
          <Typography variant='h3'>Prices</Typography>

          {isEmpty(assetSubscriptionPlan?.prices) && <NoDataAlert />}

          <Stack spacing={4}>
            {assetSubscriptionPlan?.prices?.map((price, i) => (
              <SubscriptionPlanPriceDisplay
                key={i}
                currency={assetSubscriptionPlan.currency}
                basePrice={assetSubscriptionPlan.basePrice}
                price={price}
              />
            ))}
          </Stack>
        </ViewContentBlock>

        {/* Durations */}
        <ViewContentBlock>
          <Typography variant='h3'>Durations</Typography>

          {isEmpty(assetSubscriptionPlan?.durations) && <NoDataAlert />}

          <Stack spacing={2}>
            {assetSubscriptionPlan?.durations?.map((duration, i) => (
              <Stack key={i} spacing={1}>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: `repeat(2, 1fr)`,
                    gridTemplateRows: "auto",
                    rowGap: 1,
                    columnGap: 1,
                  }}
                >
                  <FieldValue
                    label='Min duration'
                    direction='column'
                    isEmpty={!duration.minDuration}
                  >
                    <DurationDisplay value={duration.minDuration} />
                  </FieldValue>

                  <FieldValue
                    label='Discount'
                    direction='column'
                    isEmpty={isNil(duration.discount)}
                  >
                    <GeneralDiscountDisplay
                      discount={duration.discount}
                      currency={assetSubscriptionPlan?.currency}
                    />
                  </FieldValue>
                </Box>
              </Stack>
            ))}
          </Stack>
        </ViewContentBlock>

        {/* IncludedOptions */}
        <ViewContentBlock>
          <Typography variant='h3'>Included options</Typography>

          {isEmpty(assetSubscriptionPlan?.includedOptions) && <NoDataAlert />}

          {!isEmpty(assetSubscriptionPlan?.includedOptions) && (
            <SubscriptionPlanOptionList
              optionType='included'
              options={assetSubscriptionPlan?.includedOptions}
              currency={assetSubscriptionPlan?.currency}
            />
          )}
        </ViewContentBlock>

        {/* ExtraOptions */}
        <ViewContentBlock>
          <Typography variant='h3'>Extra options</Typography>

          {isEmpty(assetSubscriptionPlan?.extraOptions) && <NoDataAlert />}

          {!isEmpty(assetSubscriptionPlan?.extraOptions) && (
            <SubscriptionPlanOptionList
              optionType='extra'
              options={assetSubscriptionPlan?.extraOptions}
              currency={assetSubscriptionPlan?.currency}
            />
          )}
        </ViewContentBlock>

        {/* Toggle enabled */}
        <AssetSubscriptionPlanToggleEnabledModal
          open={isToggleEnabledModalOpen}
          onClose={() => setIsToggleEnabledModalOpen(false)}
          props={{
            assetSubscriptionPlan: assetSubscriptionPlan,
            onSave: (newValue) => {
              onUpdate(newValue);
              setIsToggleEnabledModalOpen(false);
            },
          }}
        />
      </ViewLayout>
    </PageTabContent>
  );
}
